import { Link, useNavigate } from "react-router-dom";
import { ChevronDown, LogOut } from "lucide-react";
import logo from "@/assets/logo.png";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { languageAtom } from "@/store/language";
import { useEffect } from "react";

export default function TopBar({ children }: { children?: React.ReactNode }) {
  const { token, logout, user } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [language, setLanguage] = useAtom(languageAtom);

  useEffect(() => {
    i18n.changeLanguage(language || "en");
  }, [language, i18n]);

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setLanguage(lng);
  };

  const handleProfile = () => {
    navigate("/profile", { state: { userId: user?.id } });
  };

  const handleCompany = () => {
    navigate("/my-company", { state: { companyId: user?.company?.id } });
  };

  return (
    <header className="backdrop-blur border-none shadow-card-bottom">
      <div className="w-full flex h-[80px] items-center justify-between px-4 bg-[#f5f5f5]">
        <div className="flex items-center gap-4">
          {/* Add the children at the beginning of this div to render the menu button */}
          {children}
          <Link to="/" className="flex items-center gap-2">
            <img src={logo} alt="Logo" className="h-14" />
          </Link>
        </div>
        <div className="flex justify-end items-center gap-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="bg-white rounded-full text-primary-foreground hover:bg-primary/80 hover:text-primary-foreground"
              >
                <span>{t("menu.language")}</span>
                <ChevronDown className="h-4 w-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => changeLanguage("es")}>
                Español
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => changeLanguage("en")}>
                English
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {token && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="relative text-primary-foreground hover:bg-white"
                >
                  <>
                    <Avatar className="h-8 w-8">
                      <AvatarFallback>
                        {user?.first_name?.[0]}
                        {user?.last_name?.[0]}
                      </AvatarFallback>
                    </Avatar>
                  </>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-48" align="end" forceMount>
                <DropdownMenuItem
                  onClick={handleProfile}
                  className="cursor-pointer"
                >
                  <span> {t("common.my-profile")}</span>
                </DropdownMenuItem>
                {user?.company && (
                  <DropdownMenuItem
                    onClick={handleCompany}
                    className="cursor-pointer"
                  >
                    <span> {t("common.my-company")}</span>
                  </DropdownMenuItem>
                )}
                {/* {user?.company && (
                  <DropdownMenuItem
                    onClick={handleCategories}
                    className="cursor-pointer"
                  >
                    <span> {t("common.my-categories")}</span>
                  </DropdownMenuItem>
                )} */}

                <DropdownMenuItem onClick={logout} className="cursor-pointer">
                  <LogOut className="mr-2 h-4 w-4" />
                  <span> {t("common.logout")}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </header>
  );
}
